
import React, { ReactNode } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { StickyPhone } from "@/components/ui/sticky-phone";
import { ErrorBoundary } from "./ErrorBoundary";
import { EnhancedSEO } from "@/components/seo/EnhancedSEO";
import { SEOMetadata } from "@/types/common";

interface PageLayoutProps extends SEOMetadata {
  children: ReactNode;
  noIndex?: boolean;
}

/**
 * Standard page layout component with SEO, error boundary, navbar and footer
 * @param {Object} props - Component props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {Array<string>} props.keywords - SEO keywords
 * @param {string} props.canonicalUrl - Canonical URL
 * @param {string} props.ogType - Open Graph type (optional)
 * @param {Object} props.schema - JSON-LD schema
 * @param {boolean} props.noIndex - Whether to noindex the page (defaults to false)
 * @param {ReactNode} props.children - Page content
 * @returns {JSX.Element} The PageLayout component
 */
export const PageLayout = ({
  title,
  description,
  keywords = [],
  canonicalUrl,
  ogType = "website", // Default value for ogType
  schema,
  noIndex = false, // Default to index pages (changed from true)
  children
}: PageLayoutProps) => {
  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-white flex flex-col">
        {/* Enhanced SEO component replaces Helmet usage */}
        <EnhancedSEO
          title={title}
          description={description}
          keywords={keywords}
          canonicalUrl={canonicalUrl}
          ogType={ogType as "website" | "article" | "profile" | "product" | "service"}
          schema={schema}
          noIndex={noIndex}
        />
        
        <Navbar />
        
        <main className="flex-grow relative">
          {children}
        </main>
        
        <StickyPhone />
        <Footer />
      </div>
    </ErrorBoundary>
  );
};
